/* global grecaptcha */

// src/Pages/Account.js — Account & hosting with persistent phone verification,
// strict hosting gate, and auditable Host Terms.

// Key fixes:
// - Persist phone verification across reloads (derive from auth + Firestore).
// - Avoid “provider-already-linked” when verifying same phone.
// - Safe change-number flow: reauth if needed, unlink('phone'), relink new.
// - Keep userPhones/{+E164} uniqueness index in sync (release old, claim new).
// - Require viewing Host Terms link before allowing acceptance; record view/accept.

// ---------------- React & Router ----------------
import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

// ---------------- Firebase Auth ----------------
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  signOut,
  updateProfile,
  updateEmail,
  updatePassword,
  reload,
  EmailAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  RecaptchaVerifier,
  linkWithPhoneNumber,
  PhoneAuthProvider,
  linkWithCredential,
  unlink, // for change-number flow
} from "firebase/auth";

// ---------------- Firebase Firestore/Storage ----------------
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  arrayUnion,
  runTransaction,
  deleteDoc,
} from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";

// ---------------- App imports ----------------
import { auth, db } from "../firebase";
import { useAuth } from "../lib/useAuth";
import { sendWelcomeEmail } from "../lib/notify";

/** Admin identifiers */
const ADMIN_UID = "EhQ2rwY26GY2jl1vhLNvhdBsLAa2";
const ADMIN_EMAILS = ["cjeer@jeerproperties.com"];

// Where to send users after signing out of the SPA (your Next.js landing)
const LANDING_URL =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_LANDING_URL) ||
  "https://www.yourewelcomeapp.com"; // <-- change this to your actual Next.js origin


/* ---------------- Phone helpers ---------------- */
const DEFAULT_COUNTRY = "US";

/** Strict US/E.164 normalization */
function normalizePhone(input, { defaultCountry = DEFAULT_COUNTRY } = {}) {
  if (!input) return "";
  const digits = String(input).replace(/\D/g, "");
  if (!digits) return "";
  if (defaultCountry === "US") {
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    return "";
  }
  const candidate = `+${digits.replace(/^0+/, "")}`;
  return /^\+[1-9]\d{6,14}$/.test(candidate) ? candidate : "";
}

/* ---------------- UI primitives ---------------- */
function Chip({ children, tone = "default", icon = null }) {
  const tones = {
    default: "bg-neutral-100 text-neutral-800 border-neutral-200",
    ok: "bg-green-50 text-green-800 border-green-200",
    warn: "bg-amber-50 text-amber-800 border-amber-200",
    info: "bg-[#7C9082]/10 text-neutral-800 border-[#7C9082]/30",
    admin: "bg-neutral-50 text-neutral-700 border-neutral-300",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors hover:bg-neutral-50 ${tones[tone]}`}>
      {icon}
      {children}
    </span>
  );
}

function Button({ kind = "primary", className = "", children, ...rest }) {
  let styles;
  switch (kind) {
    case "primary":
      styles = "bg-black text-white hover:bg-[#7C9082] shadow-md hover:shadow-lg active:scale-[0.98]";
      break;
    case "ghost":
      styles = "border border-neutral-300 bg-white hover:border-[#7C9082] hover:bg-neutral-50";
      break;
    case "link":
      styles = "text-[#7C9082] hover:underline px-0 py-0";
      break;
    default:
      styles = "bg-neutral-100 hover:bg-neutral-200";
  }
  return (
    <button
      {...rest}
      className={`inline-flex items-center justify-center rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed transition-all ${styles} ${className}`}
    >
      {children}
    </button>
  );
}

function Input({ className = "", ...rest }) {
  return (
    <input
      {...rest}
      className={`w-full rounded-xl border border-neutral-300 px-4 py-3 text-sm outline-none focus:border-[#7C9082] focus:ring-2 focus:ring-[#7C9082]/20 transition-all ${className}`}
    />
  );
}

function Checkbox({ checked, onChange, label, disabled }) {
  return (
    <label className={`inline-flex items-start gap-3 text-sm cursor-pointer select-none ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}>
      <input
        type="checkbox"
        className="mt-1 h-5 w-5 rounded border-neutral-300 text-[#7C9082] focus:ring-[#7C9082]/20 transition-colors"
        checked={checked}
        onChange={(e) => !disabled && onChange?.(e.target.checked)}
        disabled={disabled}
      />
      <span className="text-neutral-700">{label}</span>
    </label>
  );
}

function Collapse({ title, icon = null, children, defaultOpen = false, right = null, id }) {
  return (
    <details id={id} className="group rounded-3xl border border-neutral-200 bg-white shadow-md transition-shadow hover:shadow-lg" open={defaultOpen}>
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-3xl px-6 py-5 transition-colors hover:bg-neutral-50">
        <span className="flex items-center gap-3 text-base font-semibold">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-neutral-100 text-neutral-600">{icon}</span>
          {title}
        </span>
        <span className="ml-auto flex items-center gap-4 text-sm text-neutral-500">
          {right}
          <svg className="h-5 w-5 transition-transform group-open:rotate-180" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </span>
      </summary>
      <div className="px-6 pb-6 pt-0">{children}</div>
    </details>
  );
}

/* ---------------- Icons ---------------- */
const IconCheck = () => (<svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5" /></svg>);
const IconUser = () => (<svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21a8 8 0 10-16 0" /><circle cx="12" cy="7" r="4" /></svg>);
const IconHost = () => (<svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12l9-9 9 9" /><path d="M9 21V9h6v12" /></svg>);
const IconShield = () => (<svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>);
const IconCompass = () => (<svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M16 8l-4 8-4-4 8-4z" /></svg>);
const IconAdmin = () => (<svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3 7 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1 3-7z" /></svg>);

/* ---------------- Google Auth Button ---------------- */
function GoogleIcon() {
  return (
    <svg aria-hidden="true" className="h-6 w-6" viewBox="0 0 18 18">
      <path fill="#4285F4" d="M17.64 9.2045c0-.6381-.0573-1.2518-.1636-1.8364H9v3.4746h4.84c-.2088 1.1259-.8427 2.0805-1.7955 2.7191v2.2582h2.9082c1.7018-1.5664 2.6873-3.8737 2.6873-6.6155z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.4673-.8059 5.9564-2.1791l-2.9082-2.2582c-.8059.54-1.8373.8591-3.0482.8591-2.2 0-4.06-1.49-4.72-3.49H1.9573v2.2C3.4382 15.9827 5.4818 18 9 18z" />
      <path fill="#FBBC05" d="M4.26 10.7136c-.18-.54-.2836-1.1164-.2836-1.7136s.1036-1.1736.2836-1.7136V4.9545H1.9573C1.3477 6.1655 1 7.5455 1 9s.3477 2.8345.9573 4.0455l2.3027-1.332z" />
      <path fill="#EA4335" d="M9 3.5455c1.3218 0 2.5127.4555 3.4482 1.3491l2.5864-2.5864C13.4673.8709 11.43 0 9 0 5.4818 0 2.4382 2.0173.9573 4.9545l2.3027 1.332C3.97 4.1291 5.8564 2.5455 9 2.5455z" />
    </svg>
  );
}

function GoogleButton({ onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label="Sign in with Google"
      className="w-full inline-flex items-center justify-center gap-3 rounded-full border border-[#dadce0] bg-white px-6 py-3 text-sm font-medium text-[#3c4043] shadow-md hover:bg-neutral-50 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[#1a73e8] transition-all"
    >
      <span className="inline-flex items-center justify-center rounded bg-white"><GoogleIcon /></span>
      <span>Continue with Google</span>
    </button>
  );
}

/* ---------------- ProfileBasics ---------------- */
function ProfileBasics({ user }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [photoURL, setPhotoURL] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const avatar = useMemo(() => {
    const fallback = `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(user?.email || user?.uid || "guest")}`;
    return photoURL || user?.photoURL || fallback;
  }, [photoURL, user]);

  // ✅ Only treat Auth-linked phone as "verified" for locking this field
  const authLinkedPhone = auth.currentUser?.phoneNumber || "";
  const normalizedAuthLinkedPhone = authLinkedPhone ? normalizePhone(authLinkedPhone) : "";
  const phoneLocked = !!authLinkedPhone;

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user?.uid) return;
      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);
        const d = snap.exists() ? snap.data() : {};
        if (!alive) return;
        setDisplayName(d.displayName || user.displayName || "");
        // prefer linked phone from auth; otherwise allow draft from Firestore
        setPhone(authLinkedPhone || d.phone || d.phoneNormalized || "");
        setCompany(d.company || d.companyName || "");
        setPhotoURL(d.photoURL || user.photoURL || "");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function onSave() {
    if (!user?.uid) return;
    setSaving(true);
    setErr("");
    setMsg("");
    try {
      const normalizedInput = normalizePhone(phone || "");

      // If a verified auth phone exists, do NOT allow profile edits to override it.
      const finalPhoneRaw = authLinkedPhone ? authLinkedPhone : (phone || null);
      const finalPhoneNormalized = authLinkedPhone
        ? (normalizedAuthLinkedPhone || null)
        : (normalizedInput || null);

      const ref = doc(db, "users", user.uid);
      await setDoc(ref, {
        displayName: displayName || null,
        company: company || null,
        phone: finalPhoneRaw,
        phoneNormalized: finalPhoneNormalized,
        photoURL: photoURL || null,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      await updateProfile(auth.currentUser, {
        displayName: displayName || auth.currentUser.displayName || "",
        photoURL: photoURL || auth.currentUser.photoURL || "",
      });

      setMsg("Profile updated.");
    } catch {
      setErr("Could not save profile. Please try again.");
    } finally {
      setSaving(false);
      setTimeout(() => setMsg(""), 1600);
    }
  }

  function AvatarUploader() {
    const [uploading, setUploading] = useState(false);
    const [uploadErr, setUploadErr] = useState("");

    async function handleFile(e) {
      const file = e.target.files?.[0];
      if (!file || !user?.uid) return;
      setUploadErr("");
      setUploading(true);
      try {
        const storage = getStorage();
        const path = `users/${user.uid}/avatar_${Date.now()}`;
        const ref = storageRef(storage, path);
        await uploadBytes(ref, file, { contentType: file.type || "image/jpeg" });
        const url = await getDownloadURL(ref);
        setPhotoURL(url);
        await onSave();
      } catch (error) {
        console.warn("Avatar upload failed", error);
        setUploadErr("Upload failed. Check Storage rules and try again.");
      } finally {
        setUploading(false);
        e.target.value = "";
      }
    }

    return (
      <div className="flex items-center gap-3 mt-4">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-neutral-300 px-4 py-2 text-sm font-medium hover:border-[#7C9082] hover:bg-neutral-50 transition-all">
          <input type="file" accept="image/*" className="hidden" onChange={handleFile} />
          <span>Change photo</span>
          {uploading && <span className="text-neutral-500">Uploading…</span>}
        </label>
        {uploadErr && <span className="text-sm text-red-600">{uploadErr}</span>}
      </div>
    );
  }

  return (
    <div>
      {loading ? (
        <div className="text-sm text-neutral-500">Loading profile…</div>
      ) : (
        <>
          {(msg || err) && (
            <div className={`mb-4 rounded-xl border p-3 text-sm ${err ? "border-red-200 bg-red-50 text-red-700" : "border-green-200 bg-green-50 text-green-700"}`}>
              {err || msg}
            </div>
          )}

          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="relative flex-shrink-0">
              <img src={avatar} alt="Avatar" className="h-24 w-24 rounded-full border border-neutral-200 object-cover shadow-sm" />
              <AvatarUploader />
            </div>

            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
              <label className="text-sm font-medium text-neutral-700">
                Display name
                <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Your name" />
              </label>

              <label className="text-sm font-medium text-neutral-700">
                Phone (personal)
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 555-5555"
                  inputMode="tel"
                  disabled={phoneLocked}
                  title={phoneLocked ? "Your verified number is managed in the Hosting section." : undefined}
                />
                {phoneLocked && (
                  <span className="mt-1 block text-xs text-neutral-500">
                    To change your verified number, use Hosting → Change number.
                  </span>
                )}
              </label>

              <label className="text-sm font-medium text-neutral-700">
                Company (optional)
                <Input
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Business / LLC / Brand"
                />
              </label>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-4">
            <Button onClick={onSave} disabled={saving}>{saving ? "Saving…" : "Save changes"}</Button>
            <Button
              kind="ghost"
              onClick={() => {
                setDisplayName(user.displayName || "");
                setPhotoURL(user.photoURL || "");
                setPhone(authLinkedPhone || "");
                setCompany("");
              }}
            >
              Reset
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Security Section (email + password) ---------------- */
function SecuritySection({ user }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [emailVerified, setEmailVerified] = useState(!!user?.emailVerified);
  const [newEmail, setNewEmail] = useState(user?.email || "");
  const [currentPwForEmail, setCurrentPwForEmail] = useState("");

  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");

  async function refreshAuth() {
    if (!auth.currentUser) return;
    await reload(auth.currentUser);
    setEmailVerified(!!auth.currentUser.emailVerified);
  }

  useEffect(() => { refreshAuth(); }, []);

  function toast(ok, text) {
    setErr("");
    setMsg("");
    if (ok) setMsg(text); else setErr(text);
    setTimeout(() => { setMsg(""); setErr(""); }, 2800);
  }

  async function onResendEmailVerification() {
    if (!auth.currentUser) return;
    setBusy(true);
    try {
      await sendEmailVerification(auth.currentUser);
      toast(true, "Verification email sent.");
    } catch (e) {
      console.warn(e);
      toast(false, prettyAuthError(e));
    } finally { setBusy(false); }
  }

  async function onChangeEmail() {
    if (!auth.currentUser) return;
    if (!newEmail || newEmail === user.email) return toast(false, "Enter a different email.");
    setBusy(true);
    try {
      const isPasswordProvider = (auth.currentUser.providerData || []).some(p => p.providerId === "password");
      if (isPasswordProvider) {
        const cred = EmailAuthProvider.credential(user.email, currentPwForEmail);
        await reauthenticateWithCredential(auth.currentUser, cred);
      } else {
        const provider = new GoogleAuthProvider();
        await reauthenticateWithPopup(auth.currentUser, provider);
      }
      await updateEmail(auth.currentUser, newEmail.trim());
      await sendEmailVerification(auth.currentUser);
      await refreshAuth();
      toast(true, "Email updated. Please verify the new address.");
    } catch (e) {
      console.warn(e);
      toast(false, prettyAuthError(e));
    } finally { setBusy(false); }
  }

  async function onChangePassword() {
    if (!auth.currentUser) return;
    if (!newPw || newPw !== newPw2) return toast(false, "Passwords don't match.");
    setBusy(true);
    try {
      const isPasswordProvider = (auth.currentUser.providerData || []).some(p => p.providerId === "password");
      if (isPasswordProvider) {
        const cred = EmailAuthProvider.credential(user.email, curPw);
        await reauthenticateWithCredential(auth.currentUser, cred);
      } else {
        await sendPasswordResetEmail(auth, user.email);
        toast(true, "Reset link sent (Google/SSO account). Check your email.");
        setBusy(false);
        return;
      }
      await updatePassword(auth.currentUser, newPw);
      toast(true, "Password updated.");
      setCurPw(""); setNewPw(""); setNewPw2("");
    } catch (e) {
      console.warn(e);
      toast(false, prettyAuthError(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-6">
      {(msg || err) && (
        <div className={`rounded-xl border p-3 text-sm ${err ? "border-red-200 bg-red-50 text-red-700" : "border-green-200 bg-green-50 text-green-700"}`}>
          {err || msg}
        </div>
      )}

      {/* EMAIL verification section */}
      <div className="rounded-3xl border border-neutral-200 p-6 shadow-sm transition-shadow hover:shadow-md">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="text-base font-semibold">Email verification</div>
          <Chip tone={emailVerified ? "ok" : "warn"} icon={<IconCheck />}>
            {emailVerified ? "Verified" : "Not verified"}
          </Chip>
        </div>
        <p className="mt-2 text-sm text-neutral-600">Email verification is required to become a host.</p>

        <div className="mt-4 flex flex-wrap items-center gap-4">
          <Button kind="ghost" onClick={onResendEmailVerification} disabled={busy || emailVerified}>
            {emailVerified ? "Already verified" : "Resend verification email"}
          </Button>
          <Button kind="ghost" onClick={refreshAuth} disabled={busy}>Refresh status</Button>
        </div>

        <div className="mt-6 grid gap-4 md:max-w-lg">
          <label className="text-sm font-medium text-neutral-700">
            Change email
            <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="you@newmail.com" autoComplete="email" />
          </label>
          <label className="text-sm font-medium text-neutral-700">
            Current password (if asked)
            <Input type="password" value={currentPwForEmail} onChange={(e) => setCurrentPwForEmail(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
          </label>
          <Button onClick={onChangeEmail} disabled={busy}>Update email & verify</Button>
        </div>
      </div>

      {/* PASSWORD change section */}
      <div className="rounded-3xl border border-neutral-200 p-6 shadow-sm transition-shadow hover:shadow-md">
        <div className="text-base font-semibold">Change password</div>
        <p className="mt-2 text-sm text-neutral-600">Update your password. If you signed up with Google, we'll send a reset link instead.</p>

        <div className="mt-4 grid gap-4 md:max-w-lg">
          <label className="text-sm font-medium text-neutral-700">
            Current password
            <Input type="password" value={curPw} onChange={(e) => setCurPw(e.target.value)} placeholder="••••••••" autoComplete="current-password" />
          </label>
          <label className="text-sm font-medium text-neutral-700">
            New password
            <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="At least 6 characters" autoComplete="new-password" />
          </label>
          <label className="text-sm font-medium text-neutral-700">
            Confirm new password
            <Input type="password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} placeholder="Re-enter new password" autoComplete="new-password" />
          </label>
          <div className="flex flex-wrap gap-4">
            <Button onClick={onChangePassword} disabled={busy}>Change password</Button>
            <Button
              kind="ghost"
              onClick={async () => {
                try {
                  await sendPasswordResetEmail(auth, user.email);
                  setMsg("Password reset email sent.");
                  setTimeout(() => setMsg(""), 1600);
                } catch (e) {
                  setErr(prettyAuthError(e));
                  setTimeout(() => setErr(""), 1600);
                }
              }}
              disabled={busy}
            >
              Send reset link
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Hosting Gate ---------------- */
function HostingGate({ user, isHost, wantHost, onEnabled }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [emailVerified, setEmailVerified] = useState(!!user?.emailVerified);

  // host phone UI state
  const [hostPhoneInput, setHostPhoneInput] = useState("");
  const [changingPhone, setChangingPhone] = useState(false); // show change flow if true

  // Host Terms states
  const [hostTermsAccepted, setHostTermsAccepted] = useState(false);
  const [acceptHostTerms, setAcceptHostTerms] = useState(false);
  const [hostTermsViewedInSession, setHostTermsViewedInSession] = useState(false);

  // OTP / reCAPTCHA
  const [verificationId, setVerificationId] = useState(null);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const recaptchaVerifierRef = useRef(null);

  // phoneStatus: idle | checking | ok | taken | invalid | unknown
  const [phoneStatus, setPhoneStatus] = useState("idle");
  const [phoneStatusHint, setPhoneStatusHint] = useState("");

  function toast(ok, text) {
    setErr("");
    setMsg("");
    if (ok) setMsg(text); else setErr(text);
    setTimeout(() => { setMsg(""); setErr(""); }, 2800);
  }

  async function refreshAuth() {
    if (!auth.currentUser) return;
    await reload(auth.currentUser);
    setEmailVerified(!!auth.currentUser.emailVerified);
  }

  // Load persisted user fields (phone & terms)
  async function loadUserDoc() {
    if (!user?.uid) return;
    try {
      const ref = doc(db, "users", user.uid);
      const snap = await getDoc(ref);
      const d = snap.exists() ? (snap.data() || {}) : {};

      const alreadyAccepted = !!d.hostTermsAccepted;
      setHostTermsAccepted(alreadyAccepted);
      setAcceptHostTerms(alreadyAccepted);

      // prefer linked auth phone if exists
      const preferredPhone =
        auth.currentUser?.phoneNumber ||
        d.hostPhone ||
        d.phone ||
        d.phoneNormalized ||
        "";

      setHostPhoneInput(preferredPhone || "");

      // if phone is already linked → verified and not changing by default
      if (auth.currentUser?.phoneNumber) {
        setOtpVerified(true);
        setChangingPhone(false);
      } else {
        // no phone linked yet → show change/verify flow by default
        setChangingPhone(true);
      }
    } catch (e) {
      console.warn("Failed to load user doc for hosting gate", e);
    }
  }

  useEffect(() => { loadUserDoc(); /* eslint-disable-next-line */ }, [user]);

  const normalizedHostPhone = normalizePhone(hostPhoneInput || "");
  const hasValidPhoneFormat = !!normalizedHostPhone;

  // Re-derive OTP flags when phone changes meaningfully (only if in change/verify mode)
  useEffect(() => {
    if (!changingPhone) return;
    setOtp("");
    setOtpSent(false);
    setVerificationId(null);
    setOtpVerified(false);
  }, [normalizedHostPhone, changingPhone]);

  // Lightweight uniqueness check (userPhones/{+E164})
  useEffect(() => {
    if (!user?.uid) return;

    if (!hostPhoneInput || !hostPhoneInput.trim()) {
      setPhoneStatus("idle");
      setPhoneStatusHint("");
      return;
    }

    const normalized = normalizePhone(hostPhoneInput || "");
    if (!normalized) {
      setPhoneStatus("invalid");
      setPhoneStatusHint("That phone number doesn't look valid.");
      return;
    }

    // If the same as current linked number → treat as ok
    const currentLinked = auth.currentUser?.phoneNumber
      ? normalizePhone(auth.currentUser.phoneNumber)
      : "";

    if (currentLinked && currentLinked === normalized) {
      setPhoneStatus("ok");
      setPhoneStatusHint("This is your verified number.");
      return;
    }

    let cancelled = false;
    setPhoneStatus("checking");
    setPhoneStatusHint("Checking this number…");

    const handle = setTimeout(async () => {
      try {
        const phoneRef = doc(db, "userPhones", normalized);
        const snap = await getDoc(phoneRef);

        if (!snap.exists()) {
          if (!cancelled) {
            setPhoneStatus("ok");
            setPhoneStatusHint("This number looks good.");
          }
          return;
        }

        const data = snap.data() || {};
        if (data.uid && data.uid !== user.uid) {
          if (!cancelled) {
            setPhoneStatus("taken");
            setPhoneStatusHint("This number is already used by another account.");
          }
        } else {
          if (!cancelled) {
            setPhoneStatus("ok");
            setPhoneStatusHint("This number is already linked to your account.");
          }
        }
      } catch (e) {
        console.warn("Phone uniqueness check failed", e);
        if (!cancelled) {
          setPhoneStatus("unknown");
          setPhoneStatusHint("We couldn't check this number right now. You can still try enabling hosting.");
        }
      }
    }, 350);

    return () => { cancelled = true; clearTimeout(handle); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostPhoneInput, user?.uid]);

  // RecaptchaVerifier init
  useEffect(() => {
    if (!user || recaptchaVerifierRef.current) return;
    try {
      const verifier = new RecaptchaVerifier(auth, "recaptcha-container", {
        size: "invisible",
        callback: (response) => { console.log("reCAPTCHA invisible challenge solved.", response); },
        "expired-callback": () => {
          console.warn("reCAPTCHA expired. Please retry.");
          setOtpSent(false);
          setVerificationId(null);
          toast(false, "Verification failed (reCAPTCHA expired). Please try again.");
        },
      });
      recaptchaVerifierRef.current = verifier;
      console.log("RecaptchaVerifier initialized.");
    } catch (e) {
      console.error("Error during RecaptchaVerifier initialization:", e);
      toast(false, "Could not initialize phone verification. Please refresh the page.");
    }
    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
        console.log("RecaptchaVerifier cleared on unmount.");
      }
    };
  }, [user]);

  // Reauth helper
  async function reauthenticateIfNeeded() {
    const current = auth.currentUser;
    if (!current) return;
    try {
      // Try a no-op call that would throw requires-recent-login if needed
      await reload(current);
    } catch (_) {}
    // If later we hit requires-recent-login we’ll handle it in place.
  }

  // Unlink 'phone' if different number is already linked
  async function ensureUnlinkedIfChanging() {
    const current = auth.currentUser;
    if (!current) throw new Error("NO_USER");
    const linked = current.phoneNumber ? normalizePhone(current.phoneNumber) : "";
    if (linked && normalizedHostPhone && linked !== normalizedHostPhone) {
      try {
        await unlink(current, "phone");
      } catch (e) {
        if (e?.code === "auth/requires-recent-login") {
          // Reauth with password or Google
          const isPasswordProvider = (current.providerData || []).some(p => p.providerId === "password");
          if (isPasswordProvider) {
            throw new Error("REAUTH_REQUIRED"); // Let UI tell the user to reauth in Security
          } else {
            // Try Google popup reauth
            const provider = new GoogleAuthProvider();
            await reauthenticateWithPopup(current, provider);
            await unlink(current, "phone");
          }
        } else {
          throw e;
        }
      }
    }
  }

  // Send OTP (link or relink)
  async function sendOtp() {
    if (!auth.currentUser) return toast(false, "You must be signed in to verify your phone.");
    if (!recaptchaVerifierRef.current) return toast(false, "Verification is not ready yet. Try again.");
    if (!hasValidPhoneFormat) return toast(false, "Enter a valid phone number first.");
    if (phoneStatus === "taken") return toast(false, "That phone number is already used by another account.");

    // If same number is already linked → skip sending; mark verified.
    const currentLinked = auth.currentUser.phoneNumber ? normalizePhone(auth.currentUser.phoneNumber) : "";
    if (currentLinked && currentLinked === normalizedHostPhone) {
      setOtpVerified(true);
      setOtpSent(false);
      setVerificationId(null);
      toast(true, "Phone already verified.");
      return;
    }

    setBusy(true);
    console.log("sendOtp() clicked", {
      user: !!auth.currentUser,
      normalizedHostPhone,
      hasVerifier: !!recaptchaVerifierRef.current,
    });

    try {
      await reauthenticateIfNeeded();
      await ensureUnlinkedIfChanging();
      const confirmationResult = await linkWithPhoneNumber(
        auth.currentUser,
        normalizedHostPhone,
        recaptchaVerifierRef.current
      );
      setVerificationId(confirmationResult.verificationId);
      setOtpSent(true);
      toast(true, "Verification code sent to your phone.");
    } catch (error) {
      console.error("Error sending OTP:", error, error?.code, error?.message);
      if (error?.code === "REAUTH_REQUIRED") {
        toast(false, "For security, please reauthenticate in Security → then retry.");
      } else {
        toast(false, prettyAuthError(error));
      }
    } finally { setBusy(false); }
  }

  // Verify code (linkWithCredential)
  async function verifyOtp() {
    if (!auth.currentUser) return toast(false, "You must be signed in to verify your phone.");
    if (!verificationId) return toast(false, "Request a verification code first.");
    if (!otp || otp.length !== 6) return toast(false, "Enter the 6-digit code.");

    setBusy(true);
    try {
      const credential = PhoneAuthProvider.credential(verificationId, otp);
      await linkWithCredential(auth.currentUser, credential);
      await reload(auth.currentUser);
      setOtpVerified(true);
      setOtpSent(false);
      setVerificationId(null);

      // Persist mapping + user doc
      await claimOrMovePhoneMapping(normalizePhone(auth.currentUser.phoneNumber), hostPhoneInput);
      toast(true, "Phone verified successfully.");
    } catch (error) {
      console.error("Error verifying OTP:", error);
      toast(false, prettyAuthError(error));
    } finally { setBusy(false); }
  }

  // Keep userPhones index accurate & write user fields (atomic)
  async function claimOrMovePhoneMapping(newNormalized, rawInput) {
    const uid = user?.uid;
    if (!uid) throw new Error("NO_USER");
    if (!newNormalized) throw new Error("PHONE_INVALID");

    const previousNormalized = auth.currentUser?.metadata
      ? null
      : null; // We’ll read from users doc inside the transaction.

    await runTransaction(db, async (tx) => {
      const userRef = doc(db, "users", uid);
      const newRef = doc(db, "userPhones", newNormalized);
      const userSnap = await tx.get(userRef);
      const userData = userSnap.exists() ? (userSnap.data() || {}) : {};
      const oldNormalized = userData.phoneNormalized || "";

      // release old if owned
      if (oldNormalized && oldNormalized !== newNormalized) {
        const oldRef = doc(db, "userPhones", oldNormalized);
        const oldSnap = await tx.get(oldRef);
        if (oldSnap.exists()) {
          const d = oldSnap.data() || {};
          if (d.uid === uid) tx.delete(oldRef);
        }
      }

      // claim new
      const newSnap = await tx.get(newRef);
      if (newSnap.exists()) {
        const d = newSnap.data() || {};
        if (d.uid && d.uid !== uid) throw new Error("PHONE_TAKEN");
      }
      const nowBase = { uid, email: user.email || "", updatedAt: serverTimestamp() };
      if (!newSnap.exists()) tx.set(newRef, { ...nowBase, createdAt: serverTimestamp() });
      else tx.set(newRef, nowBase, { merge: true });

      // write user
      tx.set(userRef, {
        email: user.email || "",
        phone: rawInput || auth.currentUser?.phoneNumber || null,
        hostPhone: rawInput || auth.currentUser?.phoneNumber || null,
        phoneNormalized: newNormalized,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    });
  }

  async function markHostTermsViewed() {
    if (!user?.uid) return;
    setHostTermsViewedInSession(true);
    try {
      await setDoc(doc(db, "users", user.uid), {
        hostTermsViewedAt: serverTimestamp(),
        hostTermsVersionViewed: "v1",
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (e) {
      console.warn("Failed to write hostTermsViewedAt", e);
    }
  }

  async function enableHosting() {
    if (!user) return;
    // derive persisted state
    const alreadyLinked = !!auth.currentUser?.phoneNumber;

    if (!emailVerified) return toast(false, "Verify your email first.");
    if (!alreadyLinked && !otpVerified) return toast(false, "Verify your phone number first.");
    if (!hostTermsViewedInSession && !hostTermsAccepted) return toast(false, "Please open and view the Host Terms before agreeing.");
    if (!acceptHostTerms && !hostTermsAccepted) return toast(false, "Agree to the Host Terms & Conditions.");

    setBusy(true);
    setMsg(""); setErr("");
    try {
      // Ensure mapping exists (in case we’re relying on persisted auth state)
      const finalNormalized = normalizePhone(auth.currentUser?.phoneNumber || hostPhoneInput);
      if (!finalNormalized) throw new Error("PHONE_INVALID");
      await claimOrMovePhoneMapping(finalNormalized, hostPhoneInput);

      // Stamp acceptance
      await setDoc(doc(db, "users", user.uid), {
        roles: arrayUnion("host"),
        hostTermsAccepted: true,
        hostTermsAcceptedAt: serverTimestamp(),
        hostTermsVersion: "v1",
        updatedAt: serverTimestamp(),
      }, { merge: true });

      toast(true, "Hosting enabled.");
      onEnabled?.();
    } catch (e) {
      console.warn("Enable hosting error:", e);
      if (e?.message === "PHONE_TAKEN") toast(false, "That phone is already used by another account.");
      else if (e?.message === "PHONE_INVALID") toast(false, "Enter a valid phone number.");
      else toast(false, "Could not enable hosting. Please try again.");
    } finally { setBusy(false); }
  }

  const alreadyLinked = !!auth.currentUser?.phoneNumber;

  const phoneRequirementLabel =
    alreadyLinked || otpVerified
      ? "Phone verified"
      : phoneStatus === "ok"
      ? "Phone ready (verify via code)"
      : !hostPhoneInput
      ? "Enter phone below"
      : phoneStatus === "checking"
      ? "Checking phone…"
      : "Update phone below";

  const canSendOtp =
    !busy &&
    changingPhone &&
    hasValidPhoneFormat &&
    phoneStatus !== "taken" &&
    phoneStatus !== "invalid" &&
    phoneStatus !== "checking";

  const canEnableHosting =
    !busy &&
    emailVerified &&
    (alreadyLinked || otpVerified) &&
    (hostTermsAccepted || acceptHostTerms) &&
    (hostTermsViewedInSession || hostTermsAccepted);

  const PhoneCard = (
    <div className="rounded-3xl border border-neutral-200 p-6 shadow-sm transition-shadow hover:shadow-md">
      <div className="text-base font-semibold">
        {isHost ? "Hosting phone" : "Requirements"}
      </div>

      {!isHost && (
        <ul className="mt-4 grid gap-3 text-sm">
          <li className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Chip tone={emailVerified ? "ok" : "warn"} icon={<IconCheck />}>
              {emailVerified ? "Email verified" : "Verify email (see Security)"}
            </Chip>
            <Button kind="ghost" onClick={refreshAuth} className="sm:ml-auto">Refresh</Button>
          </li>
          <li className="flex items-center gap-3">
            <Chip
              tone={
                alreadyLinked || otpVerified
                  ? "ok"
                  : phoneStatus === "idle"
                  ? "default"
                  : phoneStatus === "ok"
                  ? "info"
                  : "warn"
              }
              icon={<IconCheck />}
            >
              {phoneRequirementLabel}
            </Chip>
          </li>
        </ul>
      )}

      {/* Verified view vs change-number flow */}
      {!changingPhone && (alreadyLinked || otpVerified) ? (
        <div className="mt-4 grid gap-2 md:max-w-md">
          <div className="text-sm text-neutral-700">
            Verified hosting phone: <span className="font-medium">{auth.currentUser?.phoneNumber}</span>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button kind="ghost" onClick={() => setChangingPhone(true)}>
              Change number
            </Button>
          </div>
          {!isHost && (
            <p className="text-xs text-neutral-500">
              If you remove or unlink your verified number, you won’t be able to enable hosting until a new number is verified.
            </p>
          )}
        </div>
      ) : (
        <div className="mt-4 grid gap-3 md:max-w-md">
          <label className="text-sm font-medium text-neutral-700">
            Hosting phone number
            <Input
              value={hostPhoneInput}
              onChange={(e) => setHostPhoneInput(e.target.value)}
              placeholder="(555) 555-5555"
              inputMode="tel"
            />
          </label>
          {phoneStatusHint && <p className="text-sm text-neutral-500">{phoneStatusHint}</p>}
          <p className="text-sm text-neutral-500">
            We use this number for booking-related contact and to keep each host unique.
          </p>

          {otpSent ? (
            <>
              <label className="text-sm font-medium text-neutral-700">
                Verification code
                <Input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="Enter 6-digit code"
                  inputMode="numeric"
                />
              </label>
              <div className="flex flex-wrap gap-3">
                <Button onClick={verifyOtp} disabled={busy || otp.length !== 6}>
                  {busy ? "Verifying..." : "Verify code"}
                </Button>
                <Button kind="ghost" onClick={sendOtp} disabled={busy}>Resend code</Button>
                <Button kind="ghost" onClick={() => { setOtpSent(false); setVerificationId(null); setOtp(""); }}>
                  Cancel
                </Button>
              </div>
            </>
          ) : (
            <div className="flex flex-wrap gap-3">
              <Button className="mt-1" onClick={sendOtp} disabled={!canSendOtp}>
                {busy ? "Sending..." : "Send verification code"}
              </Button>
              {(alreadyLinked || otpVerified) && (
                <Button kind="ghost" onClick={() => setChangingPhone(false)}>
                  Keep current number
                </Button>
              )}
              {/* Allow clearing input visually, but this will NOT allow host enabling unless a phone is verified */}
              <Button
                kind="ghost"
                onClick={() => {
                  setHostPhoneInput("");
                  setOtp("");
                  setOtpSent(false);
                  setVerificationId(null);
                  setOtpVerified(false);
                  setPhoneStatus("idle");
                  setPhoneStatusHint("");
                }}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="grid gap-4" id="host-accept">
      {(msg || err) && (
        <div className={`rounded-xl border p-3 text-sm ${err ? "border-red-200 bg-red-50 text-red-700" : "border-green-200 bg-green-50 text-green-700"}`}>
          {err || msg}
        </div>
      )}

      {/* Hidden reCAPTCHA container (required by Firebase) */}
      <div id="recaptcha-container" className="fixed bottom-0 right-0 opacity-0 pointer-events-none" />

      {isHost && (
        <div className="rounded-3xl border border-neutral-200 p-6 shadow-sm transition-shadow hover:shadow-md">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-base font-semibold">Hosting status</div>
            <Chip tone="info" icon={<IconHost />}>Host enabled</Chip>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Link to="/host"><Button>Go to host dashboard</Button></Link>
            <Link to="/host/listings/new"><Button kind="ghost">Create a listing</Button></Link>
          </div>
          <p className="mt-4 text-sm text-neutral-500">
            Your verified hosting phone is required to keep hosting active and to preserve uniqueness across accounts.
          </p>
        </div>
      )}

      {PhoneCard}

      {/* Host Terms & Conditions (only relevant to enabling host) */}
      {!isHost && (
        <>
          <div className="rounded-3xl border border-neutral-200 p-6 shadow-sm transition-shadow hover:shadow-md">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-base font-semibold">Host Terms & Conditions</div>
              <a
                href="/legal/host-terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-[#7C9082] underline underline-offset-2 hover:text-[#6C7F72]"
                onClick={markHostTermsViewed}
              >
                View terms
              </a>
            </div>
            <p className="mt-2 text-sm text-neutral-600">
              To host, you must open and review the Host Terms, then agree below.
            </p>

            <div className="mt-4">
              <Checkbox
                checked={acceptHostTerms}
                onChange={setAcceptHostTerms}
                disabled={!hostTermsViewedInSession && !hostTermsAccepted}
                label={
                  <span>
                    I agree to the{" "}
                    <a
                      href="/legal/host-terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline text-[#7C9082] hover:text-[#6C7F72]"
                      onClick={markHostTermsViewed}
                    >
                      Host Terms & Conditions
                    </a>.
                  </span>
                }
              />
              {hostTermsAccepted && (
                <p className="mt-2 flex items-center gap-2 text-sm text-green-700">
                  <IconCheck /> Host Terms already accepted on this account.
                </p>
              )}
              {!hostTermsViewedInSession && !hostTermsAccepted && (
                <p className="mt-2 text-sm text-amber-700">
                  Open “View terms” first to enable this checkbox.
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <Button onClick={enableHosting} disabled={!canEnableHosting}>
              {busy ? "Enabling…" : "Enable hosting"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Main Account component ---------------- */
export default function Account() {
  const { user, roles, loading, setMode } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();

  const params = new URLSearchParams(loc.search);
  const wantHost = params.get("becomeHost") === "1";
  const tabParam = params.get("tab");

  const [tab, setTab] = useState("signin");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const isAdmin = !!user && (user.uid === ADMIN_UID || ADMIN_EMAILS.includes(user.email || ""));
  const isHost = !!roles?.includes("host");

  useEffect(() => {
    if (user) return;
    if (tabParam === "signup" || tabParam === "signin") setTab(tabParam);
    else if (wantHost) setTab("signup");
  }, [user, tabParam, wantHost]);

  function toast(ok, text) {
    setErr(""); setMsg("");
    if (ok) setMsg(text); else setErr(text);
    setTimeout(() => { setMsg(""); setErr(""); }, 2800);
  }

  async function ensureUserDoc(u) {
    const ref = doc(db, "users", u.uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, {
        email: u.email || "",
        roles: ["guest"],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return true;
    } else {
      await updateDoc(ref, { updatedAt: serverTimestamp() });
      return false;
    }
  }

  async function handlePostLoginNavigation(u) {
    if (u && !u.emailVerified) {
      toast(false, "Please verify your email to complete setup.");
      return;
    }
    const from = loc.state?.from?.pathname;
    if (from && from.startsWith("/admin")) {
      if (isAdmin) navigate(from, { replace: true });
      else setMsg("Signed in, but this account is not an admin.");
      return;
    }
    if (wantHost) {
      setMsg("Signed in. Complete hosting setup below.");
      return;
    }
  }

  async function onEmailAuth(e) {
    e.preventDefault();
    setBusy(true);
    setErr(""); setMsg("");
    try {
      if (tab === "signin") {
        const cred = await signInWithEmailAndPassword(auth, email.trim(), pw);
        await ensureUserDoc(cred.user);
        await handlePostLoginNavigation(cred.user);
      } else {
        const cred = await createUserWithEmailAndPassword(auth, email.trim(), pw);
        const isNew = await ensureUserDoc(cred.user);
        try {
          await sendEmailVerification(cred.user);
          setMsg("Welcome! We emailed you a verification link.");
        } catch (eSendV) { console.warn("Verification email failed:", eSendV); }
        if (isNew) {
          const firstNameGuess = cred.user.displayName || (cred.user.email ? cred.user.email.split("@")[0] : "there");
          try {
            await sendWelcomeEmail({ toEmail: cred.user.email, firstName: firstNameGuess, uid: cred.user.uid });
          } catch (eSend) { console.warn("Welcome email failed:", eSend); }
        }
        await handlePostLoginNavigation(cred.user);
      }
    } catch (e2) {
      setErr(prettyAuthError(e2));
    } finally { setBusy(false); }
  }

  async function onGoogle() {
    setBusy(true);
    setErr(""); setMsg("");
    try {
      const provider = new GoogleAuthProvider();
      const cred = await signInWithPopup(auth, provider);
      const isNew = await ensureUserDoc(cred.user);
      if (isNew) {
        const firstNameGuess = cred.user.displayName || (cred.user.email ? cred.user.email.split("@")[0] : "there");
        try {
          await sendWelcomeEmail({ toEmail: cred.user.email, firstName: firstNameGuess, uid: cred.user.uid });
          setMsg("Welcome! We've emailed you a quick start guide.");
        } catch (eSend) { console.warn("Welcome email failed:", eSend); }
      }
      await handlePostLoginNavigation(cred.user);
    } catch (e2) {
      setErr(prettyAuthError(e2));
    } finally { setBusy(false); }
  }

  async function onReset() {
    setBusy(true);
    setErr(""); setMsg("");
    try {
      await sendPasswordResetEmail(auth, email.trim());
      setMsg("Password reset email sent.");
    } catch (e2) {
      setErr(prettyAuthError(e2));
    } finally { setBusy(false); }
  }

  // ------- ONLY CHANGE REQUESTED: do not redirect after sign out -------
  async function onSignOut() {
    await signOut(auth);
    // no navigate(); UI will re-render from auth listener/useAuth
  }

  if (loading) return null;

  const headerAvatar = user
    ? user.photoURL || `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(user.email || user.uid)}`
    : "https://api.dicebear.com/8.x/initials/svg?seed=Guest";

  return (
    <div className="mx-auto max-w-6xl px-4 pb-24 pt-8">
      <div className="mt-8 grid gap-8 lg:grid-cols-[320px_minmax(0,1fr)]">
        {/* LEFT: identity rail */}
        <aside className="rounded-3xl border border-neutral-200 bg-white p-8 shadow-md transition-shadow hover:shadow-lg">
          <div className="flex items-center gap-4">
            <img src={headerAvatar} alt="Avatar" className="h-20 w-20 rounded-full border border-neutral-200 object-cover shadow-sm" />
            <div className="min-w-0">
              <div className="text-lg font-semibold truncate">{user?.displayName || (user ? "Your profile" : "Guest")}</div>
              <div className="text-sm text-neutral-600 truncate">{user?.email || "Sign in to personalize"}</div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <Chip tone={user ? "ok" : "warn"} icon={<IconCheck />}>{user ? "Signed in" : "Not signed in"}</Chip>
            {!!roles?.includes("host") && (<Chip tone="info" icon={<IconHost />}>Host</Chip>)}
            {user && (user.uid === ADMIN_UID || ADMIN_EMAILS.includes(user.email || "")) && (
              <Chip tone="admin" icon={<IconAdmin />}>Admin</Chip>
            )}
          </div>

          <div className="mt-6 flex items-center gap-3">
            {user ? (
              <Button kind="link" onClick={onSignOut}>Sign out</Button>
            ) : (
              <Link to="/account?tab=signin"><Button kind="link">Sign in</Button></Link>
            )}
          </div>
        </aside>

        {/* RIGHT: content */}
        <main className="space-y-6">
          {!user ? (
            <div className="rounded-3xl border border-neutral-200 bg-white p-8 shadow-md transition-shadow hover:shadow-lg">
              <h2 className="text-2xl font-bold tracking-tight">{tab === "signin" ? "Sign in" : "Create account"}</h2>
              <p className="mt-2 text-base text-neutral-600">Use email or continue with Google.</p>

              <div className="my-6 inline-flex rounded-full border border-neutral-200 p-1 text-sm shadow-sm">
                <button className={`rounded-full px-4 py-2 ${tab === "signin" ? "bg-black text-white" : "text-neutral-700"} transition-colors`} onClick={() => setTab("signin")} type="button">Sign in</button>
                <button className={`rounded-full px-4 py-2 ${tab === "signup" ? "bg-black text-white" : "text-neutral-700"} transition-colors`} onClick={() => setTab("signup")} type="button">Create account</button>
              </div>

              {(err || msg) && (
                <div className={`mb-4 rounded-xl border p-3 text-sm ${err ? "border-red-200 bg-red-50 text-red-700" : "border-green-200 bg-green-50 text-green-700"}`}>
                  {err || msg}
                </div>
              )}

              <form onSubmit={onEmailAuth} className="grid gap-4">
                <label className="text-sm font-medium text-neutral-700">
                  Email
                  <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" />
                </label>
                <label className="text-sm font-medium text-neutral-700">
                  Password
                  <div className="relative">
                    <Input
                      type={showPw ? "text" : "password"}
                      required
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      placeholder="••••••••"
                      autoComplete={tab === "signin" ? "current-password" : "new-password"}
                    />
                    <button
                      type="button"
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-neutral-600 hover:text-[#7C9082]"
                      onClick={() => setShowPw((v) => !v)}
                      aria-label={showPw ? "Hide password" : "Show password"}
                    >
                      {showPw ? "Hide" : "Show"}
                    </button>
                  </div>
                </label>

                <Button type="submit" disabled={busy}>{busy ? "Please wait…" : tab === "signin" ? "Sign in" : "Create account"}</Button>

                <div className="relative my-4 text-center text-sm text-neutral-500">
                  <span className="relative z-10 bg-white px-3">or</span>
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 border-t border-neutral-200" />
                </div>

                <GoogleButton onClick={onGoogle} disabled={busy} />

                {tab === "signin" && (
                  <button
                    type="button"
                    onClick={onReset}
                    disabled={busy || !email}
                    className="text-sm text-neutral-600 underline disabled:opacity-60 hover:text-[#7C9082] transition-colors text-left"
                  >
                    Forgot password?
                  </button>
                )}
              </form>
            </div>
          ) : (
            <>
              {(err || msg) && (
                <div className={`rounded-xl border p-3 text-sm ${err ? "border-red-200 bg-red-50 text-red-700" : "border-green-200 bg-green-50 text-green-700"}`}>
                  {err || msg}
                </div>
              )}

              {!user.emailVerified && (
                <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6 text-base text-amber-800 shadow-sm">
                  Please verify your email to finish setting up your account. Use the controls in the Security section below to resend the link, then click "Refresh status".
                </div>
              )}

              <Collapse title="Profile & Contact" icon={<IconUser />} defaultOpen>
                <ProfileBasics user={user} />
                <div className="mt-6 flex flex-wrap gap-4">
                  <Link to="/messages"><Button kind="ghost">Messages</Button></Link>
                  <Link to="/trips" className="sm:hidden"><Button kind="ghost">Bookings</Button></Link>
                  <a href="/legal/privacy"><Button kind="ghost">Privacy</Button></a>
                </div>
              </Collapse>

              <Collapse
                title={isHost ? "Hosting" : "Become a host"}
                icon={<IconHost />}
                defaultOpen={isHost || wantHost}
                right={isHost ? (<Chip tone="info" icon={<IconCheck />}>Enabled</Chip>) : null}
              >
                <HostingGate
                  user={user}
                  isHost={isHost}
                  wantHost={wantHost}
                  onEnabled={() => { setMode?.("host"); navigate("/host", { replace: true }); }}
                />
              </Collapse>

              <Collapse title="Security" icon={<IconShield />} id="security-section" defaultOpen={wantHost}>
                <SecuritySection user={user} />
              </Collapse>

              <Collapse title="Discover" icon={<IconCompass />}>
                <div className="flex flex-wrap gap-4">
                  <Link to="/explore"><Button kind="ghost">Explore spaces</Button></Link>
                  <Link to="/trips"><Button kind="ghost">Your bookings</Button></Link>
                </div>
              </Collapse>

              {user && isAdmin && (
                <Collapse title="Admin tools" icon={<IconAdmin />} right={<Chip tone="admin" icon={<IconAdmin />}>Admin</Chip>}>
                  <div className="flex flex-wrap items-center gap-4">
                    <Link to="/admin"><Button>Open admin dashboard</Button></Link>
                    <Link to="/admin/listings"><Button kind="ghost">Review listings</Button></Link>
                  </div>
                  <p className="mt-4 text-sm text-neutral-500">
                    If you were redirected here from an admin page, signing in again with this account will take you straight back to admin.
                  </p>
                </Collapse>
              )}
            </>
          )}
        </main>
      </div>

      {/* Sticky CTA for existing hosts (mobile) */}
      {user && isHost && (
        <div className="sm:hidden fixed inset-x-0 bottom-0 z-20 border-t border-neutral-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 shadow-lg">
          <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-4">
            <Link to="/host" className="flex-1"><Button className="w-full">Host dashboard</Button></Link>
            <Link to="/host/listings/new"><Button kind="ghost">New listing</Button></Link>
          </div>
        </div>
      )}

      <p className="mt-8 text-center text-sm text-neutral-500">
        By continuing you agree to our{" "}
        <a href="/legal/terms" className="underline hover:text-[#7C9082]">Terms</a> &{" "}
        <a href="/legal/privacy" className="underline hover:text-[#7C9082]">Privacy Policy</a>.
      </p>
    </div>
  );
}

function prettyAuthError(e) {
  const code = e?.code || "";
  const map = {
    "auth/invalid-credential": "Incorrect email or password.",
    "auth/user-not-found": "No account found with that email.",
    "auth/wrong-password": "Incorrect email or password.",
    "auth/weak-password": "Password should be at least 6 characters.",
    "auth/email-already-in-use": "That email is already in use.",
    "auth/popup-closed-by-user": "Sign-in popup was closed.",
    "auth/cancelled-popup-request": "Popup cancelled. Try again.",
    "auth/too-many-requests": "Too many attempts. Your device/IP/number may be temporarily throttled. Wait and retry.",
    "auth/quota-exceeded": "Quota exceeded right now. Please try again in a little while.",
    "auth/network-request-failed": "Network issue. Check your connection or ad/cookie blockers and try again.",
    "auth/operation-not-allowed": "Phone authentication is not enabled for this Firebase project.",
    "auth/missing-verification-code": "Missing code.",
    "auth/invalid-verification-code": "Invalid code.",
    "auth/code-expired": "The code has expired. Request a new one.",
    "auth/invalid-phone-number": "Enter a valid phone number.",
    "auth/provider-already-linked": "A phone number is already linked to this account.",
    "auth/credential-already-in-use": "That phone number is already linked to a different account.",
    "auth/account-exists-with-different-credential": "That phone number belongs to another account.",
    "auth/requires-recent-login": "Please reauthenticate and try again.",
    "auth/argument-error": "Phone verification isn't available right now. Refresh and try again.",
    "auth/invalid-app-credential": "Verification failed. Check domain authorization and disable ad blockers.",
    "auth/captcha-check-failed": "Verification failed. Please refresh the page and try again.",
    "auth/invalid-app-id": "The app identifier is not registered for the current project.",
    "auth/missing-app-credential": "The request is missing a client identifier.",
    "auth/invalid-cert-hash": "The certificate hash provided is invalid.",
    "auth/missing-client-identifier": "The request is missing a client identifier.",
    "auth/invalid-multi-factor-session": "The request does not contain a multi-factor session.",
  };
  if (!code && `${e || ""}`.includes("400")) return "Phone verification failed (400). Try again.";
  return map[code] || `Something went wrong (${code || "unknown error"}). Please try again.`;
}
